import type {
  DraggableListQuestion,
  MultipleChoiceQuestion,
  Question,
  RatingQuestion,
  ShortTextQuestion,
  TextQuestion,
} from '@shared/domain/form';
import type { LegalCheckBindings } from './question_types/ReviewTextQuestion';
import { QuestionType } from '@shared/domain/form';
import { useResponsive } from '@shared/hooks/useResponsive';
import React from 'react';
import { shouldShowQuestion } from '../helpers/ReviewFormHelpers';
import ReviewDraggableListQuestion from './question_types/ReviewDraggableListQuestion';
import MultipleChoiceReviewQuestion from './question_types/ReviewMultipleChoiceQuestion';
import ReviewRatingQuestion from './question_types/ReviewRatingQuestion';
import ReviewShortTextQuestion from './question_types/ReviewShortTextQuestion';
import ReviewTextArea from './question_types/ReviewTextQuestion';

interface QuestionRendererProps {
  questions: Question[];
  formFields: any;
  errors: Record<string, string>;
  handleFormFieldChange: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  extendFormFields?: (fields: Record<string, any>) => void;
  validateField?: (name: string, value: any) => void;
  setFormFields?: any;
  disabledOptionsGetter?: (questionName: string) => string[] | undefined;
  handleRatingChange?: (name: string, value: number | null) => void;
  currentReview?: any;
  getLegalCheckBindings?: (questionName: string) => LegalCheckBindings | undefined;
}

export function QuestionRenderer({
  questions,
  formFields,
  errors,
  handleFormFieldChange,
  extendFormFields,
  validateField,
  setFormFields,
  disabledOptionsGetter,
  handleRatingChange,
  currentReview,
  getLegalCheckBindings,
}: QuestionRendererProps) {
  const { isMobile } = useResponsive();

  // A question is "conditional" if it only appears once a prior answer
  // unlocks it. We pass that to each question component so it can use a
  // more pronounced mount animation + glow than the default subtle fade.
  const isConditionalQuestion = (question: Question): boolean =>
    !!question.conditionallyShow ||
    (Array.isArray(question.conditionalShowRules) && question.conditionalShowRules.length > 0);

  const renderQuestion = (question: Question, index: number) => {
    const isConditional = isConditionalQuestion(question);
    const commonProps = {
      'data-error-name': question.name,
      question,
      formFields,
      errors,
      isConditional,
    };

    switch (question.type) {
      case QuestionType.SHORT_TEXT:
        return (
          <ReviewShortTextQuestion
            key={question.name}
            {...commonProps}
            question={question as ShortTextQuestion}
            handleFormFieldChange={handleFormFieldChange}
          />
        );

      case QuestionType.MULTIPLE_CHOICE: {
        const multipleChoiceQuestion = question as MultipleChoiceQuestion;
        return (
          <MultipleChoiceReviewQuestion
            key={question.name}
            {...commonProps}
            question={multipleChoiceQuestion}
            disabledOptions={disabledOptionsGetter?.(question.name)}
            handleFormFieldChange={
              extendFormFields && validateField
                ? (e) => {
                    extendFormFields({ [question.name]: e.currentTarget.value });
                    validateField(question.name, e.currentTarget.value);
                  }
                : handleFormFieldChange
            }
            isNarrow={multipleChoiceQuestion.options.length > 8}
          />
        );
      }

      case QuestionType.TEXT:
        return (
          <ReviewTextArea
            key={question.name}
            {...commonProps}
            question={question as TextQuestion}
            handleFormFieldChange={handleFormFieldChange}
            helperText={question.helperText}
            index={index}
            legalCheck={getLegalCheckBindings?.(question.name)}
          />
        );

      case QuestionType.RATING:
        return (
          <ReviewRatingQuestion
            key={question.name}
            {...commonProps}
            question={question as RatingQuestion}
            handleRatingChange={handleRatingChange || (() => {})}
            currentReview={currentReview}
          />
        );

      case QuestionType.DRAGGABLE_LIST:
        return (
          <ReviewDraggableListQuestion
            key={question.name}
            {...commonProps}
            question={question as DraggableListQuestion}
            helperText={question.helperText}
            handleFormFieldChange={setFormFields || (() => {})}
          />
        );

      default:
        return null;
    }
  };

  // Helper function to check if a question is a text-based question
  const isTextQuestion = (question: Question) =>
    question.type === QuestionType.TEXT || question.type === QuestionType.SHORT_TEXT;

  // Helper function to check if a question is a rating question
  const isRatingQuestion = (question: Question) => question.type === QuestionType.RATING;

  // First filter out questions that shouldn't be shown based on conditional rules
  const visibleQuestions = questions.filter((q) => shouldShowQuestion(q, formFields));

  // Group consecutive questions for responsive layout
  const renderQuestions = () => {
    if (isMobile) {
      // On mobile, render all questions in single column
      return visibleQuestions.map((question, index) => (
        <div key={question.name} style={{ marginBottom: '1rem' }}>
          {renderQuestion(question, index)}
        </div>
      ));
    }

    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < visibleQuestions.length) {
      const currentQuestion = visibleQuestions[i];

      if (isRatingQuestion(currentQuestion)) {
        // Collect consecutive rating questions for grid layout
        const ratingQuestions = [currentQuestion];
        let j = i + 1;

        while (j < visibleQuestions.length && isRatingQuestion(visibleQuestions[j])) {
          ratingQuestions.push(visibleQuestions[j]);
          j++;
        }

        // Render rating questions in responsive grid
        elements.push(
          <div
            key={`rating-grid-${i}`}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1rem',
              marginBottom: '1rem',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}
          >
            {ratingQuestions.map((question, index) => (
              <div
                key={question.name}
                style={{
                  minWidth: 0,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                }}
              >
                {renderQuestion(question, i + index)}
              </div>
            ))}
          </div>,
        );

        i = j; // Move to the next non-rating question
      } else if (currentQuestion.type === QuestionType.TEXT) {
        // Collect every consecutive long-form TEXT question into a single
        // .review-text-grid block. The grid uses CSS subgrid to align the
        // prompt / helper / textarea rows across all columns — so a 1-line
        // prompt sitting next to a 2-line prompt no longer leaves the right
        // textarea floating higher than the left. SHORT_TEXT is intentionally
        // excluded; it gets a separate single-column rendering below.
        const textGroup = [currentQuestion];
        let j = i + 1;
        while (j < visibleQuestions.length && visibleQuestions[j].type === QuestionType.TEXT) {
          textGroup.push(visibleQuestions[j]);
          j++;
        }

        elements.push(
          <div key={`text-grid-${currentQuestion.name}`} className="review-text-grid">
            {textGroup.map((q, qi) => renderQuestion(q, i + qi))}
          </div>,
        );

        i = j;
      } else if (isTextQuestion(currentQuestion)) {
        // SHORT_TEXT or any other text-shaped question — render on its own row.
        elements.push(
          <div key={currentQuestion.name} style={{ marginBottom: '1rem' }}>
            {renderQuestion(currentQuestion, i)}
          </div>,
        );
        i += 1;
      } else {
        // Render other question types normally
        elements.push(
          <div key={currentQuestion.name} style={{ marginBottom: '1rem' }}>
            {renderQuestion(currentQuestion, i)}
          </div>,
        );
        i += 1;
      }
    }

    return elements;
  };

  return <>{renderQuestions()}</>;
}
